import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref, FunctionComponent } from 'react';

import { useI18n, useTestIds } from '../../../../hooks';
import FormField from '../../../FormField';
import type { ForwardProps } from '../../../../types';
import { getDurationInputTestIds } from '../../DateTime.test-ids';
import { createClassName, withTestIds } from '../../../../utils';
import useAutoFocusNextInput from '../useAutoFocusNextInput';

import Time from './Time';
import NumberUnit from './NumberUnit';
import type DurationInputProps from './DurationInput.types';
import { Format } from './DurationInput.types';

const DurationInput: FunctionComponent<DurationInputProps & ForwardProps> = forwardRef(
  function DurationInput(
    props: PropsWithoutRef<DurationInputProps>,
    ref: Ref<HTMLFieldSetElement>
  ) {
    const {
      testId,
      id,
      label,
      labelHidden,
      required,
      status,
      disabled,
      onChange,
      format = Format.Seconds,
      onSelect,
      additionalInfo,
      className: classNameProp,
      ...restProps
    } = props;
    const className = createClassName('duration-input', classNameProp);
    const testIds = useTestIds(testId, getDurationInputTestIds);

    const t = useI18n();

    // Refs are actually handled in Time component rendered below.
    const { description, descriptionID } = useAutoFocusNextInput([]);

    const Comp =
      format === Format.Formatted ? (
        <Time
          testId={testId}
          status={status}
          disabled={disabled}
          onChange={onChange}
          {...restProps}
          className={className}
        />
      ) : (
        <NumberUnit
          testId={testId}
          format={format}
          status={status}
          disabled={disabled}
          onChange={onChange}
          onSelect={onSelect}
          {...restProps}
          className={className}
        />
      );

    return label ? (
      <>
        <FormField
          as='fieldset'
          {...(format === Format.Formatted ? { info: t('time_format_info') } : undefined)}
          status={status}
          testId={testIds}
          label={label}
          labelHidden={labelHidden}
          id={id}
          required={required}
          disabled={disabled}
          ref={ref}
          additionalInfo={additionalInfo}
          aria-describedby={format === Format.Formatted ? descriptionID : undefined}
        >
          {Comp}
        </FormField>
        {format === Format.Formatted ? description : null}
      </>
    ) : (
      Comp
    );
  }
);

export default withTestIds(DurationInput, getDurationInputTestIds);
